import Vue from 'vue';
import Web3Modal from 'web3modal';
import { ethers } from 'ethers';
import WalletConnectProvider from '@walletconnect/web3-provider';

const state = {
  activeAccount: null,
  activeBalance: 0,
  chainId: null,
  chainName: null,
  providerEthers: null, // this is "provider" for Ethers.js
  isConnected: false,
  providerW3m: null, // this is "provider" from Web3Modal
  web3Modal: null,
  accountData: {
    hasPopulated: false,
    isWhitelisted: false,
    allowedMintQuantity: 0,
    mintedQuantity: 0,
  },
  isMinting: false,
};

const getters = {
  getActiveAccount(state) {
    return state.activeAccount;
  },
  getActiveAccountChecksum(state) {
    return state.activeAccount !== null
      ? ethers.utils.getAddress(state.activeAccount)
      : state.activeAccount;
  },
  getActiveAccountRedacted(state, getters) {
    const activeAccount = getters['getActiveAccountChecksum'];
    return `${activeAccount.slice(0, 6)}...${activeAccount.slice(-4)}`;
  },
  getActiveBalanceWei(state) {
    return state.activeBalance;
  },
  getActiveBalanceEth(state) {
    return ethers.utils.formatEther(state.activeBalance);
  },
  getChainId(state) {
    return state.chainId;
  },
  getChainName(state) {
    return state.chainName;
  },
  getProviderEthers(state) {
    return state.providerEthers;
  },
  getWeb3Modal(state) {
    return state.web3Modal;
  },
  isUserConnected(state) {
    return state.isConnected;
  },
  getAccountData(state) {
    return state.accountData;
  },
  getIsMinting(state) {
    return state.isMinting;
  },
};

const actions = {
  async initWeb3Modal({ commit }) {
    const providerOptions = {
      walletconnect: {
        package: WalletConnectProvider, // required
        options: {
          infuraId: process.env.VUE_APP_INFURA_PROJECT_ID, // required
        },
      },
    };

    const w3mObject = new Web3Modal({
      providerOptions, // required
    });

    commit('setWeb3ModalInstance', w3mObject);
  },

  async connectWeb3Modal({ commit, dispatch }) {
    if (state.web3Modal === null) {
      await dispatch('initWeb3Modal');
    }
    let providerW3m = await state.web3Modal.connect();
    const accounts = await providerW3m.request({ method: 'eth_accounts' });
    commit('setIsConnected', true);

    commit('setActiveAccount', accounts[0]);
    commit('setChainData', await providerW3m.request({ method: 'eth_chainId' }));
    commit('setEthersProvider', providerW3m);
    actions.fetchActiveBalance({ commit });
    dispatch('ethereumListener');
  },

  async disconnectWeb3Modal({ commit }) {
    commit('disconnectWallet');
    commit('setIsConnected', false);
  },

  async ethereumListener({ commit }) {
    state.providerW3m.on('accountsChanged', (accounts) => {
      if (state.isConnected) {
        commit('setActiveAccount', accounts[0]);
        commit('setEthersProvider', state.providerW3m);
        actions.fetchActiveBalance({ commit });
      }
    });

    state.providerW3m.on('chainChanged', (chainId) => {
      commit('setChainData', chainId);
      commit('setEthersProvider', state.providerW3m);
      actions.fetchActiveBalance({ commit });
    });
  },

  async fetchActiveBalance({ commit }) {
    let balance = await state.providerEthers.getBalance(state.activeAccount);
    commit('setActiveBalance', balance);
  },

  resetAccount({ commit }) {
    const accountData = {
      hasPopulated: false,
    };

    commit('setAccountData', accountData);
  },
};

const mutations = {
  async disconnectWallet(state) {
    state.activeAccount = null;
    state.activeBalance = 0;
    state.providerEthers = null;
    if (state.providerW3m.close && state.providerW3m !== null) {
      await state.providerW3m.close();
    }
    state.providerW3m = null;
    await state.web3Modal.clearCachedProvider();
  },

  setActiveAccount(state, selectedAddress) {
    state.activeAccount = selectedAddress;
  },

  setActiveBalance(state, balance) {
    state.activeBalance = balance;
  },

  setChainData(state, chainId) {
    state.chainId = chainId;

    switch (chainId) {
      case '0x1':
        state.chainName = 'Mainnet';
        break;
      case '0x2a':
        state.chainName = 'Kovan';
        break;
      case '0x3':
        state.chainName = 'Ropsten';
        break;
      case '0x4':
        state.chainName = 'Rinkeby';
        break;
      case '0x5':
        state.chainName = 'Goerli';
        break;
      case '0x539': // 1337 (often used on localhost)
      case '0x1691': // 5777 (default in Ganache)
      default:
        state.chainName = 'Localhost';
        break;
    }
  },

  async setEthersProvider(state, providerW3m) {
    state.providerW3m = providerW3m;
    state.providerEthers = new ethers.providers.Web3Provider(providerW3m);
  },

  setIsConnected(state, isConnected) {
    state.isConnected = isConnected;
  },

  setWeb3ModalInstance(state, w3mObject) {
    state.web3Modal = w3mObject;
  },

  setAccountData(state, accountData) {
    Vue.set(state, 'accountData', accountData);
  },

  setIsMinting(state, isMinting) {
    state.isMinting = isMinting;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
